import React from 'react';
import { motion } from 'framer-motion';
import { SnowflakeIcon, FlameIcon } from './Icons';
export function MechanismSection() {
  return <section className="w-full bg-sage-light py-10 md:py-24 px-5" data-id="element-390">
      <div className="max-w-2xl mx-auto" data-id="element-391">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.7
      }} className="text-center mb-7 md:mb-12" data-id="element-392">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-md mb-6" data-id="element-393">
            <SnowflakeIcon className="w-8 h-8 text-sage" data-id="element-394" />
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-brown-dark mb-4" data-id="element-395">
            El Método de Enfriamiento Foliar
          </h2>
          <p className="font-body text-warm-gray text-base" data-id="element-396">
            Un enfoque completamente diferente
          </p>
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6,
        delay: 0.15
      }} className="font-body text-lg md:text-xl text-brown-medium leading-relaxed mb-8" data-id="element-397">
          En lugar de "estimular" un folículo que ya está estresado, este
          protocolo utiliza la{' '}
          <strong className="text-brown-dark" data-id="element-398">vasoconstricción térmica</strong>{' '}
          y botánicos específicos para bajar la temperatura del cuero cabelludo.
        </motion.p>

        {/* Visual process */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6,
        delay: 0.25
      }} className="flex items-center justify-center gap-4 md:gap-6 my-10" data-id="element-399">
          <div className="flex flex-col items-center gap-2" data-id="element-400">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-red-100 flex items-center justify-center" data-id="element-401">
              <FlameIcon className="w-7 h-7 text-red-500" data-id="element-402" />
            </div>
            <span className="font-body text-sm text-warm-gray font-medium" data-id="element-403">
              Calor
            </span>
          </div>

          <div className="flex-shrink-0" data-id="element-404">
            <svg className="w-8 h-8 text-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-405">
              <line x1="5" y1="12" x2="19" y2="12" data-id="element-406" />
              <polyline points="12 5 19 12 12 19" data-id="element-407" />
            </svg>
          </div>

          <div className="flex flex-col items-center gap-2" data-id="element-408">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-409">
              <SnowflakeIcon className="w-7 h-7 text-blue-500" data-id="element-410" />
            </div>
            <span className="font-body text-sm text-warm-gray font-medium" data-id="element-411">
              Enfriar
            </span>
          </div>

          <div className="flex-shrink-0" data-id="element-412">
            <svg className="w-8 h-8 text-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-413">
              <line x1="5" y1="12" x2="19" y2="12" data-id="element-414" />
              <polyline points="12 5 19 12 12 19" data-id="element-415" />
            </svg>
          </div>

          <div className="flex flex-col items-center gap-2" data-id="element-416">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-green-100 flex items-center justify-center" data-id="element-417">
              <svg className="w-7 h-7 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-418">
                <path d="M12 22c-4.97 0-7-2-7-5s2-5 7-5 7 2 7 5-2.03 5-7 5z" data-id="element-419" />
                <path d="M12 12V2" data-id="element-420" />
                <path d="M8 6l4-4 4 4" data-id="element-421" />
              </svg>
            </div>
            <span className="font-body text-sm text-warm-gray font-medium" data-id="element-422">
              Recuperar
            </span>
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6,
        delay: 0.35
      }} className="font-body text-lg md:text-xl text-brown-medium leading-relaxed mb-8" data-id="element-423">
          Al enfriar el tejido, el poro se contrae, la inflamación se disipa y
          el folículo recupera su fuerza de anclaje original.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        scale: 0.97
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="bg-white rounded-xl p-6 md:p-8 border border-sage/20 shadow-sm" data-id="element-424">
          <p className="font-heading text-xl md:text-2xl text-brown-dark leading-relaxed italic text-center" data-id="element-425">
            "Es como poner hielo en una inflamación: el alivio es inmediato y la
            recuperación comienza al instante."
          </p>
        </motion.div>
      </div>
    </section>;
}